// =============================================================================
// io/sync/wipeImported.ts — "Wipe All Imported".
// -----------------------------------------------------------------------------
// Takes back every parser-imported trade so the broker files can be dragged in
// again from scratch. The rule for what that means is in
// `domain/importedTrades.ts` — read it first; the interesting part is which of
// two marks decides, and why this control uses the OTHER one from Import
// History.
//
// ── 🚨 WHY THIS EXISTS WHEN UNDO IMPORT ALREADY DOES ──
//
// Undo Import is per BATCH, and a batch only exists for a trade this client's
// importer stamped. `io/sync/importBatches.ts` says in its own header that
// unstamped trades "never cross the wire", and that on a book where only recent
// imports are stamped that is most of it.
//
// ▶ So on a book carried over from beta, **Import History cannot see, let alone
// undo, the majority of what a parser wrote.** This is the only control that
// reaches those rows, and that is the whole reason it is worth building rather
// than pointing the user at the undo they already have.
//
// ── 🚨 READ, THEN CONFIRM, THEN DELETE THE EXACT IDS THAT WERE COUNTED ──
//
// Not `DELETE … WHERE imported`. `undoImportBatch` states the doctrine and it
// holds harder here because the scope is the whole book: **the ids are the
// consent.** A delete that re-derives its own target between the confirmation
// and the write can remove rows the user never saw a number for — an import
// that lands from a logger mid-confirmation being the obvious way.
//
// ⚠ It also sidesteps a filter this module would otherwise have to trust
// blind. Expressing "imported AND NOT synthetic" as a PostgREST delete means
// `synthetic_close=not.is.true`, whose null handling decides whether a legacy
// row is exempt — and getting it backwards deletes rows beta cannot rebuild.
// Reading first turns that into a visible count instead of a silent one.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';
import { planWipe, type WipeScope } from '../../domain/importedTrades';
import { reportFailureAs } from '../diagnostics/errorLog';

/**
 * ⚠ THREE COLUMNS. `id` to delete by, and the two marks the rule reads.
 * Reading the whole row for every trade in the book to reach two booleans is
 * the fat version of this — same argument `importBatches.ts` makes for its own
 * narrow select.
 */
const SCOPE_SELECT = 'id,imported,synthetic_close';

/**
 * What a wipe would take, and what it would keep.
 *
 * ⚠ THROWS on a failed read rather than returning a short scope. A partial read
 * here becomes a confirmation that understates what is about to happen, and the
 * user agrees to a number that is not the number.
 */
export async function planWipeImported(sb: SupabaseClient): Promise<WipeScope> {
  const rows: Record<string, unknown>[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    // ⚠ ORDERED, for the reason `exportAccount.readAll` sets out: `range()` over
    // an unordered query has no defined page boundary, so a row written
    // mid-read shifts the window and one is silently dropped. Dropping one here
    // means a trade the user was told would go, staying.
    const { data, error } = await sb
      .from('trades')
      .select(SCOPE_SELECT)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    if (error) {
      throw new Error(
        `Couldn’t work out what would be removed (page ${(from / PAGE_ROWS) + 1}): ${error.message}`,
      );
    }
    const page = (data ?? []) as unknown as Record<string, unknown>[];
    if (!page.length) break;
    rows.push(...page);
    if (page.length < PAGE_ROWS) break;
  }

  return planWipe(rows);
}

export interface WipeResult {
  deleted: number;
  error: string | null;
}

/**
 * Delete the counted ids.
 *
 * ⚠ Chunked, and a partial delete is REPORTED with its count — same shape as
 * `undoImportBatch`, for the same reason: half a wipe is a book in a state
 * nobody chose, and "how far did it get" is the first question the user has.
 *
 * ⚠ THIS TAKES ANNOTATIONS WITH IT. A trade imported in March and graded in June
 * loses the grade, the notes and the pattern. The caller says so before asking.
 */
export async function wipeImported(
  sb: SupabaseClient,
  ids: readonly string[],
): Promise<WipeResult> {
  if (!ids.length) return { deleted: 0, error: null };

  const CHUNK = 200;
  let deleted = 0;

  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK) as string[];
    const { error } = await sb.from('trades').delete().in('id', slice);

    if (error) {
      return {
        deleted,
        error: reportFailureAs(
          'wipe imported',
          error,
          `Stopped after removing ${deleted} of ${ids.length} imported trade`
          + `${ids.length === 1 ? '' : 's'}: ${error.message}. The rest are still in your `
          + `book — run it again to finish.`,
        ),
      };
    }
    deleted += slice.length;
  }

  return { deleted, error: null };
}
